import { Plus, Trash2, Info } from "lucide-react";
import { PRODUCT_PAGE } from "@/constants/testIds";

export default function MOQTab({ data, onChange }) {
  const tiers = data.moq_tiers || [];

  const addTier = () => {
    onChange("moq_tiers", [...tiers, { min_qty: 0, price: 0 }]);
  };

  const updateTier = (idx, field, rawValue) => {
    // Allow empty string while typing; parse only when value is present
    const value = rawValue === "" ? 0 : (field === "min_qty" ? parseInt(rawValue, 10) || 0 : parseFloat(rawValue) || 0);
    const updated = tiers.map((t, i) =>
      i === idx ? { ...t, [field]: value } : t
    );
    onChange("moq_tiers", updated);
  };

  const removeTier = (idx) => {
    onChange("moq_tiers", tiers.filter((_, i) => i !== idx));
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-5">
        <div className="w-5 h-5 bg-orange-100 rounded flex items-center justify-center">
          <span className="text-orange-600 text-xs font-bold">5</span>
        </div>
        <h2 className="text-base font-semibold text-slate-900">MOQ</h2>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-orange-100 rounded flex items-center justify-center flex-shrink-0">
              <span className="text-orange-600 text-xs">$</span>
            </div>
            <h3 className="text-sm font-semibold text-slate-800">MOQ Wise Pricing</h3>
          </div>
        </div>

        {/* Info */}
        <div className="px-5 py-3 bg-blue-50 border-b border-blue-100 flex items-start gap-2">
          <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-blue-700">
            Marketplace commission and cost calculation. Quantity 1 is supported.{" "}
            <strong>Bulk tiers will apply automatically on storefront.</strong>
          </p>
        </div>

        <div className="p-5">
          {/* Table */}
          <div className="border border-slate-200 rounded-xl overflow-hidden mb-4" data-testid={PRODUCT_PAGE.bulkPricingTable}>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Minimum Quantity
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Price (₹)
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider w-12">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {tiers.map((tier, idx) => (
                  <tr key={idx} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-2.5">
                      <input
                        type="text"
                        inputMode="numeric"
                        value={tier.min_qty > 0 ? tier.min_qty : ""}
                        onChange={e => updateTier(idx, "min_qty", e.target.value)}
                        className="w-32 px-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 text-slate-900"
                        placeholder="e.g. 1, 5, 10"
                      />
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="relative w-32">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">₹</span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={tier.price || ""}
                          onChange={e => updateTier(idx, "price", e.target.value)}
                          className="w-full pl-7 pr-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 text-slate-900"
                          placeholder="0"
                        />
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <button
                        type="button"
                        onClick={() => removeTier(idx)}
                        disabled={tiers.length === 1}
                        className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all disabled:opacity-30"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
                {tiers.length === 0 && (
                  <tr>
                    <td colSpan={3} className="px-4 py-8 text-center text-slate-400 text-sm">
                      No pricing tiers. Add tiers to enable bulk pricing.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <button
            type="button"
            onClick={addTier}
            className="flex items-center gap-1.5 text-sm text-orange-600 hover:text-orange-700 font-medium border border-orange-300 hover:border-orange-400 px-3 py-1.5 rounded-lg transition-all"
          >
            <Plus className="w-4 h-4" />
            Add Pricing Tier
          </button>

          {/* Preview */}
          {tiers.length > 1 && (
            <div className="mt-4 p-3 bg-orange-50 rounded-lg">
              <p className="text-xs font-medium text-orange-700 mb-2">Bulk Pricing Preview (Storefront)</p>
              <div className="flex flex-wrap gap-2">
                {tiers.map((tier, idx) => (
                  <div key={idx} className="text-xs bg-white border border-orange-200 rounded px-2 py-1">
                    <span className="text-slate-600">
                      {tier.min_qty}
                      {tiers[idx + 1] ? `–${tiers[idx + 1].min_qty - 1}` : "+"}
                    </span>{" "}
                    <span className="font-semibold text-orange-600">₹{tier.price}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
